import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Badge,
    LinearProgress,
    Avatar,
    Stack,
    Divider,
    Drawer,
    Switch,
    FormControlLabel,
    Autocomplete,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    ShoppingCart as OrderIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    LocalShipping as ShippingIcon,
    Assignment as DetailIcon,
    Timeline as TrendIcon,
    Storefront as MarketplaceIcon,
    PriorityHigh as PriorityIcon,
    Email as EmailIcon,
    LocationOn as AddressIcon,
    FormatListBulleted as ItemsIcon,
    Receipt as ReceiptIcon,
    AttachMoney as RevenueIcon,
    ViewKanban as KanbanIcon,
    TableChart as TableIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    useOrders,
    useOrderStats,
    useCreateOrder,
    useUpdateOrder,
    useDeleteOrder,
    useMarketplaces
} from '../../hooks/useOrders';
import { useProducts } from '../../hooks/useProducts';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import KanbanView, { ORDER_KANBAN_COLUMNS } from '../../components/ui/KanbanView';
import { ViewSelector } from '../../components/ui/CustomViewsManager';
import useCustomViews from '../../hooks/useCustomViews';
import {
  SUPPORTED_CURRENCIES,
  getCurrencySymbol,
} from '../../utils/invoice-pdf-general-section-utilities';

/**
 * Aurora 2026 Order Fulfillment Cockpit
 * 
 * High-fidelity unified interface for Order Lifecycle Management.
 * Features SmartDataGrid for rapid fulfillment orchestration and Glass Sidebar for complex order configurations.
 */
const OrderCockpitPage = () => {
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    // Row click handler to navigate to order details
    const handleRowClick = useCallback((order) => {
        if (order?.id) {
            navigate(`/admin/orders/${order.id}`);
        }
    }, [navigate]);

    // UI State
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('table');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [marketplaceFilter, setMarketplaceFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [dateRangeFilter, setDateRangeFilter] = useState('all');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [formTab, setFormTab] = useState(0);
    const [selectedCurrency, setSelectedCurrency] = useState('EUR');

    // Data Hooks
    const { data: ordersData, isLoading, refetch } = useOrders({
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        marketplace: marketplaceFilter !== 'all' ? marketplaceFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
        date_range: dateRangeFilter !== 'all' ? dateRangeFilter : undefined
    });
    const { data: statsRaw } = useOrderStats();
    const { data: marketplacesData } = useMarketplaces();

    const createMutation = useCreateOrder();
    const updateMutation = useUpdateOrder();
    const deleteMutation = useDeleteOrder();

    // Custom Views Hook for saving filter configurations
    const {
        views,
        selectedView,
        selectView,
        createView,
        updateView,
        deleteView,
        toggleFavorite
    } = useCustomViews({ storageKey: 'wms_order_cockpit_views' });

    // Apply view filters when a saved view is selected
    const handleViewSelect = useCallback((view) => {
        selectView(view.id);
        if (view.filters) {
            if (view.filters.status) setStatusFilter(view.filters.status);
            if (view.filters.marketplace) setMarketplaceFilter(view.filters.marketplace);
            if (view.filters.priority) setPriorityFilter(view.filters.priority);
            if (view.filters.dateRange) setDateRangeFilter(view.filters.dateRange);
            if (view.filters.search) setSearchTerm(view.filters.search);
        }
    }, [selectView]);

    // Save current filters as a new view
    const handleSaveCurrentView = useCallback((viewName) => {
        const newView = {
            name: viewName,
            filters: {
                status: statusFilter,
                marketplace: marketplaceFilter,
                priority: priorityFilter,
                dateRange: dateRangeFilter,
                search: searchTerm
            },
            columns: [],
            sorting: []
        };
        createView(newView);
        enqueueSnackbar(t('common.order.viewSaved', 'View saved successfully'), { variant: 'success' });
    }, [statusFilter, marketplaceFilter, priorityFilter, dateRangeFilter, searchTerm, createView, enqueueSnackbar, t]);

    // Form State (Industrial Fidelity)
    const [formData, setFormData] = useState({
        orderNumber: '',
        customerName: '',
        customerEmail: '',
        status: 'DRAFT',
        priority: 'NORMAL',
        marketplace: '',
        items: [],
        shippingAddress: {
            street: '',
            city: '',
            postalCode: '',
            country: ''
        },
        notes: ''
    });


    const [itemForm, setItemForm] = useState({ product: null, quantity: 1 });
    const { data: productsData } = useProducts({ limit: 100 });
    const products = useMemo(() => productsData?.items || [], [productsData]);

    const handleAddItem = () => {
        if (!itemForm.product || itemForm.quantity < 1) return;
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_id: itemForm.product.id,
                product_name: itemForm.product.name,
                sku: itemForm.product.sku,
                quantity: parseInt(itemForm.quantity),
                unit_price: itemForm.product.price || 0
            }]
        }));
        setItemForm({ product: null, quantity: 1 });
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const orders = useMemo(() => ordersData?.items || [], [ordersData]);
    const stats = useMemo(() => statsRaw || {}, [statsRaw]);
    const marketplaces = useMemo(() => marketplacesData?.marketplaces || [], [marketplacesData]);

    const handleRefresh = useCallback(() => refetch(), [refetch]);

    const handleOpenDrawer = useCallback((order = null) => {
        if (order) {
            setSelectedOrder(order);
            setFormData({
                orderNumber: order.orderNumber || '',
                customerName: order.customerName || '',
                customerEmail: order.customerEmail || '',
                status: order.status || 'DRAFT',
                priority: order.priority || 'NORMAL',
                marketplace: order.marketplace || '',
                items: order.items || [],
                shippingAddress: order.shippingAddress || {
                    street: '',
                    city: '',
                    postalCode: '',
                    country: ''
                },
                notes: order.notes || ''
            });
        } else {
            setSelectedOrder(null);
            setFormData({
                orderNumber: '',
                customerName: '',
                customerEmail: '',
                status: 'DRAFT',
                priority: 'NORMAL',
                marketplace: '',
                items: [],
                shippingAddress: { street: '', city: '', postalCode: '', country: '' },
                notes: ''
            });
        }
        setDrawerOpen(true);
        setFormTab(0);
    }, []);

    const handleCloseDrawer = () => setDrawerOpen(false);

    const handleFormChange = (field, subfield = null) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        if (subfield) {
            setFormData(prev => ({
                ...prev,
                [field]: { ...prev[field], [subfield]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (selectedOrder) {
                await updateMutation.mutateAsync({ orderId: selectedOrder.id, data: formData });
                enqueueSnackbar('Order updated successfully', { variant: 'success' });
            } else {
                await createMutation.mutateAsync(formData);
                enqueueSnackbar('Order created successfully', { variant: 'success' });
            }
            handleCloseDrawer();
            refetch();
        } catch (error) {
            enqueueSnackbar(`Operation Failed: ${error.message}`, { variant: 'error' });
        }
    };

    const handleDeleteClick = (order) => {
        setSelectedOrder(order);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await deleteMutation.mutateAsync(selectedOrder.id);
            enqueueSnackbar('Order cancelled and archived', { variant: 'success' });
            setDeleteDialogOpen(false);
            refetch();
        } catch (error) {
            enqueueSnackbar(`Cancellation Error: ${error.message}`, { variant: 'error' });
        }
    };

    const handleKanbanStatusChange = useCallback(async (order, newStatus) => {
        try {
            await updateMutation.mutateAsync({ 
                orderId: order.id, 
                data: { ...order, status: newStatus.toUpperCase() } 
            });
            enqueueSnackbar(t('common.order.statusUpdated', 'Order status updated to {{status}}', { status: newStatus }), { variant: 'success' });
            refetch();
        } catch (error) {
            enqueueSnackbar(`${t('common.order.statusUpdateFailed', 'Status update failed')}: ${error.message}`, { variant: 'error' });
        }
    }, [updateMutation, refetch, enqueueSnackbar, t]);

    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    }, []);

    const getStatusColor = (status) => {
        const colors = {
            'DRAFT': 'default',
            'PENDING': 'warning',
            'PROCESSING': 'info',
            'READY_TO_SHIP': 'secondary',
            'SHIPPED': 'primary',
            'DELIVERED': 'success',
            'CANCELLED': 'error',
            'ON_HOLD': 'warning'
        };
        return colors[status] || 'default';
    };

    const formatCurrency = useCallback((amount) => {
        const symbol = getCurrencySymbol(selectedCurrency);
        return new Intl.NumberFormat(i18n.language || 'en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount || 0).replace(/^/, symbol);
    }, [selectedCurrency, i18n.language]);

        // SmartDataGrid Columns
        const columns = [
            {
                id: 'order',
                label: t('common.order.orderReference', 'Order Reference'),
                render: (val, row) => (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar variant="rounded" sx={{ bgcolor: 'primary.soft', color: 'primary.main' }}>
                            <OrderIcon fontSize="small" />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.orderNumber}</Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(row.created_at).toLocaleDateString()}</Typography>
                        </Box>
                    </Stack>
                )
            },
            {
                id: 'customer',
                label: t('common.order.fulfillmentTarget', 'Fulfillment Target'),
                render: (val, row) => (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.customerName}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.customerEmail}</Typography>
                    </Box>
                )
            },
            {
                id: 'value',
                label: t('common.order.economicValue', 'Economic Value'),
                render: (val, row) => (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(row.total_amount || 0)}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.lineItemsCount || row.line_items_count || row.items?.length || 0} {t('common.lineItems', 'Line Items')}</Typography>
                    </Box>
                )
            },
            {
                id: 'marketplace',
                label: t('common.order.originChannel', 'Origin Channel'),
                render: (val, row) => (
                    <Chip
                        label={row.marketplace || '-'}
                        size="small"
                        variant="outlined"
                        icon={<MarketplaceIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{ borderRadius: '6px' }}
                    />
                )
            },
            {
                id: 'status',
                label: t('common.order.executionStatus', 'Execution Status'),
                render: (val, row) => (
                    <Chip
                        label={row.status}
                        size="small"
                        color={getStatusColor(row.status)}
                        sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '10px' }}
                    />
                )
            },
            {
                id: 'actions',
                label: t('common.orchestration', 'Orchestration'),
            align: 'right',
            render: (val, row) => (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title={t('common.lifecycleEdit', 'Lifecycle Edit')}><IconButton size="small" color="primary" onClick={() => handleOpenDrawer(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={t('common.cancelOrder', 'Cancel Order')}><IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ p: 4, background: 'rgba(244, 247, 254, 0.4)', minHeight: '100vh' }}>
            {/* High-Fidelity Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48, boxShadow: '0 4px 12px rgba(67, 24, 255, 0.2)' }}>
                            <OrderIcon fontSize="large" />
                        </Avatar>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
                            {t('common.order.cockpitTitle', 'Order Cockpit')}
                        </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ ml: 8 }}>
                        {t('common.order.cockpitSubtitle', 'Real-time Fulfillment Orchestration & Global Order Lifecycle Control.')}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>{t('common.order.refetchTelemetry', 'Refetch Telemetry')}</Button>
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDrawer()} sx={{ boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)' }}>{t('common.order.injectOrder', 'Inject Order')}</Button>
                </Stack>
            </Box>

            {/* Performance Pulse KPIs */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                    { label: t('common.order.totalVolume', 'Total Volume'), value: stats.total_orders || 0, color: '#4318FF', icon: <OrderIcon />, trend: '+4.2%' },
                    { label: t('common.order.awaitingExecution', 'Awaiting Execution'), value: stats.pending_orders || 0, color: '#FFB547', icon: <PriorityIcon />, trend: t('common.critical', 'Critical') },
                    { label: t('common.order.grossRevenue', 'Gross Revenue'), value: formatCurrency(stats.total_revenue || 0), color: '#01B574', icon: <RevenueIcon />, trend: '+12.5%' },
                    { label: t('common.order.avgOrderValue', 'Avg Order Value'), value: formatCurrency(stats.average_order_value || 0), color: '#EE5D50', icon: <TrendIcon />, trend: t('common.stable', 'Stable') }
                ].map((stat, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card sx={{ borderRadius: '20px', border: 'none', boxShadow: '0px 18px 40px rgba(112, 144, 176, 0.12)' }}>
                            <CardContent sx={{ p: '24px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ width: 56, height: 56, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(244, 247, 254, 1)', color: stat.color }}>
                                        {stat.icon}
                                    </Box>
                                    <Typography variant="caption" sx={{ color: stat.trend.includes('+') ? 'success.main' : stat.trend === 'Critical' ? 'error.main' : 'text.secondary', fontWeight: 700 }}>
                                        {stat.trend}
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{stat.value}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Main Operational Table */}
            <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: '24px', boxShadow: '0px 18px 40px rgba(112, 144, 176, 0.08)', border: '1px solid rgba(233, 239, 250, 0.5)' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #E9EFFA', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                            <Tab label={t('common.order.activeStream', 'Active Stream')} sx={{ fontWeight: 700 }} />
                            <Tab label={t('common.order.backorders', 'Backorders')} sx={{ fontWeight: 700 }} />
                            <Tab label={t('common.order.archived', 'Archived')} sx={{ fontWeight: 700 }} />
                        </Tabs>
                        <Divider orientation="vertical" flexItem />
                        <ViewSelector
                            views={views}
                            selectedView={selectedView}
                            onSelectView={handleViewSelect}
                            onSaveView={handleSaveCurrentView}
                            onDeleteView={deleteView}
                            onToggleFavorite={toggleFavorite}
                            onUpdateView={updateView}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>{t('common.order.status', 'Status')}</InputLabel>
                            <Select value={statusFilter} label={t('common.order.status', 'Status')} onChange={(e) => setStatusFilter(e.target.value)}>
                                <MenuItem value="all">{t('common.order.allStages', 'All Stages')}</MenuItem>
                                <MenuItem value="DRAFT">Draft</MenuItem>
                                <MenuItem value="PENDING">Pending</MenuItem>
                                <MenuItem value="PROCESSING">Processing</MenuItem>
                                <MenuItem value="READY_TO_SHIP">Ready to Ship</MenuItem>
                                <MenuItem value="SHIPPED">Shipped</MenuItem>
                                <MenuItem value="DELIVERED">Delivered</MenuItem>
                                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                                <MenuItem value="ON_HOLD">{t('common.order.onHold', 'On Hold')}</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>{t('common.order.marketplace', 'Marketplace')}</InputLabel>
                            <Select value={marketplaceFilter} label={t('common.order.marketplace', 'Marketplace')} onChange={(e) => setMarketplaceFilter(e.target.value)}>
                                <MenuItem value="all">{t('common.order.allChannels', 'All Channels')}</MenuItem>
                                {marketplaces.map(m => <MenuItem key={m.id || m.name} value={m.name}>{m.name}</MenuItem>)}
                                <MenuItem value="DIRECT">Direct / Manual</MenuItem>
                                <MenuItem value="AMAZON">Amazon</MenuItem>
                                <MenuItem value="EBAY">eBay</MenuItem>
                                <MenuItem value="SHOPIFY">Shopify</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>{t('common.currency', 'Currency')}</InputLabel>
                            <Select value={selectedCurrency} label={t('common.currency', 'Currency')} onChange={(e) => setSelectedCurrency(e.target.value)}>
                                {SUPPORTED_CURRENCIES.map((currency) => (
                                    <MenuItem key={currency} value={currency}>
                                        {getCurrencySymbol(currency)} {currency}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>{t('common.order.priority', 'Priority')}</InputLabel>
                            <Select value={priorityFilter} label={t('common.order.priority', 'Priority')} onChange={(e) => setPriorityFilter(e.target.value)}>
                                <MenuItem value="all">{t('common.order.allPriorities', 'All Priorities')}</MenuItem>
                                <MenuItem value="LOW">Economy</MenuItem>
                                <MenuItem value="NORMAL">Standard</MenuItem>
                                <MenuItem value="HIGH">Expedited</MenuItem>
                                <MenuItem value="URGENT">Critical</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>{t('common.order.dateRange', 'Date Range')}</InputLabel>
                            <Select value={dateRangeFilter} label={t('common.order.dateRange', 'Date Range')} onChange={(e) => setDateRangeFilter(e.target.value)}>
                                <MenuItem value="all">{t('common.order.allTime', 'All Time')}</MenuItem>
                                <MenuItem value="today">{t('common.order.today', 'Today')}</MenuItem>
                                <MenuItem value="week">{t('common.order.thisWeek', 'This Week')}</MenuItem>
                                <MenuItem value="month">{t('common.order.thisMonth', 'This Month')}</MenuItem>
                                <MenuItem value="quarter">{t('common.order.thisQuarter', 'This Quarter')}</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            placeholder="Search orders..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            sx={{ minWidth: 200 }}
                        />
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewModeChange}
                            size="small"
                            aria-label={t('common.order.viewMode', 'View Mode')}
                        >
                            <ToggleButton value="table" aria-label={t('common.order.tableView', 'Table View')}>
                                <Tooltip title={t('common.order.tableView', 'Table View')}>
                                    <TableIcon fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="kanban" aria-label={t('common.order.kanbanView', 'Kanban View')}>
                                <Tooltip title={t('common.order.kanbanView', 'Kanban View')}>
                                    <KanbanIcon fontSize="small" />
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Box>
                {isLoading && <LinearProgress color="primary" />}
                {viewMode === 'table' ? (
                    <SmartDataGrid
                        columns={columns}
                        data={orders}
                        loading={isLoading}
                        density="comfortable"
                        onRowClick={handleRowClick}
                        sx={{ '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(67, 24, 255, 0.02)', cursor: 'pointer' } }}
                    />
                ) : (
                    <Box sx={{ p: 2, overflowX: 'auto' }}>
                        <KanbanView
                            columns={ORDER_KANBAN_COLUMNS}
                            items={orders.map(order => ({
                                ...order,
                                title: order.orderNumber,
                                description: `${order.customerName} - ${formatCurrency(order.total_amount || 0)}`,
                                assignee: order.customerName,
                                amount: order.total_amount,
                                tags: order.marketplace ? [order.marketplace] : []
                            }))}
                            groupBy="status"
                            onStatusChange={handleKanbanStatusChange}
                            onView={(order) => navigate(`/admin/orders/${order.id}`)}
                            onEdit={handleOpenDrawer}
                            onDelete={handleDeleteClick}
                            isLoading={isLoading}
                        />
                    </Box>
                )}
            </Paper>

            {/* Industrial Glass Sidebar */}
            <Drawer
                anchor="right" open={drawerOpen} onClose={handleCloseDrawer}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 700 },
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(12px)',
                        boxShadow: '-10px 0px 50px rgba(0,0,0,0.1)',
                        borderLeft: '1px solid rgba(255,255,255,0.3)'
                    }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(90deg, rgba(67, 24, 255, 0.05) 0%, rgba(255,255,255,0) 100%)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><OrderIcon /></Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800 }}>{selectedOrder ? `${t('common.order.order', 'Order')} ${formData.orderNumber}` : t('common.order.newOrderIntent', 'New Order Intent')}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <DetailIcon fontSize="inherit" /> {t('common.order.lifecycleManagement', 'Full Lifecycle Management Interface')}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleCloseDrawer} sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}><CloseIcon /></IconButton>
                    </Box>

                    <Box sx={{ px: 4, py: 2 }}>
                        <Tabs
                            value={formTab} onChange={(e, v) => setFormTab(v)}
                            variant="scrollable" scrollButtons="auto"
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label={t('common.order.entityDetails', 'Entity Details')} icon={<OrderIcon />} iconPosition="start" />
                            <Tab label={t('common.order.logisticsOps', 'Logistics & Ops')} icon={<ShippingIcon />} iconPosition="start" />
                            <Tab label={t('common.order.lineItems', 'Line Items')} icon={<ItemsIcon />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    <Box sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
                        <form id="order-form" onSubmit={handleSave}>
                            {formTab === 0 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label={t('common.order.orderReference', 'Order Reference #')} required value={formData.orderNumber} onChange={handleFormChange('orderNumber')} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t('common.order.originMarketplace', 'Origin Marketplace')}</InputLabel>
                                            <Select value={formData.marketplace} label={t('common.order.originMarketplace', 'Origin Marketplace')} onChange={handleFormChange('marketplace')}>
                                                <MenuItem value="">{t('common.order.directManual', 'Direct / Manual')}</MenuItem>
                                                {marketplaces.map(m => <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label={t('common.order.customerFullName', 'Customer Full Name')} required value={formData.customerName} onChange={handleFormChange('customerName')} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label={t('common.order.customerEmail', 'Customer Email Registry')} required value={formData.customerEmail} onChange={handleFormChange('customerEmail')} InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t('common.order.lifecycleStage', 'Current Lifecycle Stage')}</InputLabel>
                                            <Select value={formData.status} label={t('common.order.lifecycleStage', 'Current Lifecycle Stage')} onChange={handleFormChange('status')}>
                                                <MenuItem value="DRAFT">{t('common.order.status.draft', 'Draft')}</MenuItem>
                                                <MenuItem value="PENDING">{t('common.order.status.awaitingVerification', 'Awaiting Verification')}</MenuItem>
                                                <MenuItem value="PROCESSING">{t('common.order.status.inPreparation', 'In Preparation')}</MenuItem>
                                                <MenuItem value="READY_TO_SHIP">{t('common.order.status.stagedForCarrier', 'Staged for Carrier')}</MenuItem>
                                                <MenuItem value="SHIPPED">{t('common.order.status.inTransit', 'In Transit')}</MenuItem>
                                                <MenuItem value="DELIVERED">{t('common.order.status.fulfillmentComplete', 'Fulfillment Complete')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t('common.order.fulfillmentPriority', 'Fulfillment Priority')}</InputLabel>
                                            <Select value={formData.priority} label={t('common.order.fulfillmentPriority', 'Fulfillment Priority')} onChange={handleFormChange('priority')}>
                                                <MenuItem value="LOW">{t('common.order.priority.economy', 'Economy')}</MenuItem>
                                                <MenuItem value="NORMAL">{t('common.order.priority.standard', 'Standard')}</MenuItem>
                                                <MenuItem value="HIGH">{t('common.order.priority.expedited', 'Expedited')}</MenuItem>
                                                <MenuItem value="URGENT">{t('common.order.priority.critical', 'Critical / Next Day')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            )}

                            {formTab === 1 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}><AddressIcon fontSize="small" /> {t('common.order.deliveryDestination', 'Delivery Destination')}</Typography></Grid>
                                    <Grid item xs={12}><TextField fullWidth label={t('common.order.streetAddress', 'Street & Infrastructure #')} value={formData.shippingAddress.street} onChange={handleFormChange('shippingAddress', 'street')} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label={t('common.order.city', 'City / Metropolitan Area')} value={formData.shippingAddress.city} onChange={handleFormChange('shippingAddress', 'city')} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label={t('common.order.postalCode', 'Postal / ZIP Code')} value={formData.shippingAddress.postalCode} onChange={handleFormChange('shippingAddress', 'postalCode')} /></Grid>
                                    <Grid item xs={12}><TextField fullWidth label={t('common.order.country', 'Country / Global Region')} value={formData.shippingAddress.country} onChange={handleFormChange('shippingAddress', 'country')} /></Grid>
                                    <Divider sx={{ width: '100%', my: 2 }} />
                                    <Grid item xs={12}><TextField fullWidth multiline rows={4} label={t('common.order.fulfillmentNotes', 'Fulfillment Notes / Carrier Instructions')} value={formData.notes} onChange={handleFormChange('notes')} /></Grid>
                                </Grid>
                            )}

                            {formTab === 2 && (
                                <Box>
                                    <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>{t('common.order.addLineItem', 'Add Line Item')}</Typography>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Autocomplete
                                                options={products}
                                                getOptionLabel={(option) => `${option.sku} - ${option.name}`}
                                                value={itemForm.product}
                                                onChange={(_, newValue) => setItemForm({ ...itemForm, product: newValue })}
                                                renderInput={(params) => <TextField {...params} label={t('common.order.selectProduct', 'Select Product')} size="small" />}
                                                sx={{ flex: 1 }}
                                            />
                                            <TextField
                                                type="number"
                                                label={t('common.qty', 'Qty')}
                                                size="small"
                                                sx={{ width: 100 }}
                                                value={itemForm.quantity}
                                                onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                                            />
                                            <Button variant="contained" onClick={handleAddItem} disabled={!itemForm.product}>{t('common.add', 'Add')}</Button>
                                        </Stack>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {t('common.order.currentPayload', 'Current payload includes {{count}} entities.', { count: formData.items?.length || 0 })}
                                    </Typography>

                                    <Stack spacing={2}>
                                        {formData.items.map((item, idx) => (
                                            <Paper key={idx} sx={{ p: 2, background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Box>
                                                        <Typography variant="subtitle2">{item.product_name || item.sku}</Typography>
                                                        <Typography variant="caption" color="text.secondary">SKU: {item.sku}</Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Chip label={`Qty: ${item.quantity}`} size="small" />
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveItem(idx)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </form>
                    </Box>

                    <Box sx={{ p: 4, borderTop: '1px solid #E9EFFA', bgcolor: '#fff' }}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" size="large" onClick={handleCloseDrawer} sx={{ borderRadius: '12px' }}>{t('common.discard', 'Discard')}</Button>
                            <Button
                                type="submit" form="order-form"
                                variant="contained" color="primary" size="large"
                                startIcon={<SaveIcon />}
                                disabled={createMutation.isPending || updateMutation.isPending}
                                sx={{ borderRadius: '12px', px: 4, bgcolor: 'primary.main', fontWeight: 700 }}
                            >
                                {selectedOrder ? t('common.order.commitConfiguration', 'Commit Configuration') : t('common.order.initializeLifecycle', 'Initialize Lifecycle')}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Drawer>

            {/* Deletion Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>{t('common.order.terminateFulfillment', 'Terminate Order Fulfillment?')}</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        {t('common.order.cancelConfirmation', 'Are you sure you want to cancel Order #{{orderNumber}}? This will suspend picking and shipping operations. Financial reversal may be required.', { orderNumber: selectedOrder?.orderNumber })}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="text" color="inherit">{t('common.order.retainOrder', 'Retain Order')}</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete} sx={{ borderRadius: '12px', px: 3 }}>{t('common.order.abortLifecycle', 'Abort Lifecycle')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default React.memo(OrderCockpitPage);
